import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowDownTrayIcon, LockClosedIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { publicLinksApi } from '../api/public-links';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

export function PublicDownloadPage() {
  const { t } = useTranslation('public');
  const { token } = useParams<{ token: string }>();
  const [linkInfo, setLinkInfo] = useState<{
    fileName?: string;
    hasPassword: boolean;
    isExpired: boolean;
    remainingDownloads: number | null;
  } | null>(null);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadInfo = async () => {
      if (!token) return;
      try {
        const info = await publicLinksApi.getInfo(token);
        setLinkInfo(info);
      } catch {
        setError(t('download.linkNotFoundDesc'));
      } finally {
        setIsLoading(false);
      }
    };
    loadInfo();
  }, [token, t]);

  const handleDownload = async () => {
    if (!token) return;
    setIsDownloading(true);
    setError('');

    try {
      const { downloadUrl, fileName } = await publicLinksApi.download(
        token,
        password || undefined
      );

      // Trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err: any) {
      if (err.status === 401) {
        setError(t('download.incorrectPassword'));
      } else if (err.status === 410) {
        setError(t('download.linkExpiredDesc'));
      } else {
        setError(t('download.downloadFailed'));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && !linkInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('download.linkNotFound')}</h1>
          <p className="text-gray-500 dark:text-slate-400">{error}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 max-w-md w-full"
      >
        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl shadow-lg mb-4">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">{t('download.title')}</h1>
        </div>

        {/* File info */}
        <div className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex items-center justify-center">
              <DocumentIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 dark:text-white truncate">
                {linkInfo?.fileName || t('download.file')}
              </p>
              {linkInfo?.remainingDownloads !== null && linkInfo?.remainingDownloads !== undefined && (
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  {t('download.downloadsRemaining', { count: linkInfo?.remainingDownloads })}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Password input */}
        {linkInfo?.hasPassword && (
          <div className="mb-6">
            <Input
              type="password"
              label={t('download.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('download.enterPassword')}
              leftIcon={<LockClosedIcon className="w-5 h-5" />}
              error={error.includes('password') || error.includes('Contraseña') ? error : undefined}
            />
          </div>
        )}

        {/* Error */}
        {error && !error.includes('password') && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-4 py-3 rounded-lg text-sm mb-4"
          >
            {error}
          </motion.div>
        )}

        {/* Download button */}
        <Button
          onClick={handleDownload}
          isLoading={isDownloading}
          disabled={linkInfo?.isExpired}
          className="w-full"
          size="lg"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
          {linkInfo?.isExpired ? t('download.linkExpired') : t('download.downloadButton')}
        </Button>

        <p className="text-center text-xs text-gray-400 dark:text-slate-500 mt-6">
          {t('download.sharedVia')}
        </p>
      </motion.div>
    </div>
  );
}
